import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { MemberType } from 'api/models';
import { TaskType } from 'api/tasks';
import { SquadValue } from 'components/molecules/squad-picker';
import { addDocumentFromTemplateAction } from 'data/templates/documents';
import {
  ProjectTemplateTask,
  updateProjectTemplateParentOfTask,
  useGetProjectPhaseOfTask,
  useHasSetupSpacesInTemplate,
  useProjectTemplateSpaces,
  useProjectTemplateSpaceTabs,
} from 'data/templates/projects';
import {
  addTaskAboveProjectTemplate,
  addTaskBelowProjectTemplate,
  addTaskDependencyInProjectTemplate,
  createSubTaskInProjectTemplate,
  createTaskInProjectTemplate,
  deleteTaskInProjectTemplate,
  updateJiraMappingsInTemplateAction,
  UpdateJiraMappingsInTemplateAction,
  updateTaskInProjectTemplate,
} from 'data/templates/projects/actions';
import { queueMacrotask } from 'utils/macrotask';
import { floatUid } from 'utils/uid';
import {
  MentionItem,
  MentionType,
} from 'vendor/ckeditor/plugins/mention/types';

const getGridAutoColumnElement = (id: number): HTMLElement | undefined =>
  document.querySelector(
    `[row-id="${id}"] [col-id="ag-Grid-AutoColumn"]`,
  ) as HTMLElement;

export const openGridAutoColumnCell = (id: number) => {
  window.setTimeout(() => {
    getGridAutoColumnElement(id)?.click();
  }, 50);
};

type UseTemplateTaskCommonActions = (task: ProjectTemplateTask) => {
  addSubTask(): void;
  onToggleTaskType(): void;
  onTogglePrivate(): void;
  onUpdateName(name: string): Promise<void>;
  onToggleTaskTemplate(): void;
  onUpdateNameAndAddRow(name: string): Promise<void>;
  onUpdateDescription(description: string): void;
  onUpdatePrivateNote(description: string): void;
  onDeleteTask(): void;
  onUpdateTask(payload: Partial<ProjectTemplateTask>): void;
  onUpdateParent({ id, type }: { id: number; type: string }): void;
  onAddTaskBelow(): void;
  onAddTaskAbove(): void;
  onUpdateAssignee(assignee?: SquadValue): void;
  onCreateDependency(dependentTask: ProjectTemplateTask): void;
  onUpdateCSATState(checked: boolean): void;
  onAddMention(
    data: any,
    { isContextPrivate }: { isContextPrivate: boolean },
  ): void;
  onChangeResponsibility(value: MemberType[]): void;
  onJiraFormChange(data: UpdateJiraMappingsInTemplateAction): void;
};

export const useTemplateTaskCommonActions: UseTemplateTaskCommonActions = (
  task,
) => {
  const dispatch = useDispatch();
  const spaceSetup = useHasSetupSpacesInTemplate();
  const taskProjectPhase = useGetProjectPhaseOfTask(task.taskId);
  const spacesOfTemplate = useProjectTemplateSpaceTabs();
  const spaces = Object.values(useProjectTemplateSpaces());
  const taskIdRef = useRef(task.taskId);

  useEffect(() => {
    taskIdRef.current = task.taskId;
  }, [task.taskId]);

  const update = (payload: Partial<ProjectTemplateTask>) => {
    dispatch(
      updateTaskInProjectTemplate({ taskId: taskIdRef.current, payload }),
    );
  };

  const onDeleteTask = () => {
    dispatch(deleteTaskInProjectTemplate({ taskId: task.taskId }));
  };

  const onUpdateName = (taskName: string) => {
    update({ taskName });

    return Promise.resolve();
  };

  const onToggleTaskTemplate = () => update({ isTemplate: !task.isTemplate });

  const onUpdateAssignee = (assignee) => {
    update({ assignee });
  };

  const onUpdateNameAndAddRow = (taskName: string) => {
    onUpdateName(taskName);

    queueMacrotask(() => {
      if (task.projectPhase) {
        dispatch(
          createTaskInProjectTemplate({
            projectPhaseId: task.projectPhase.projectPhaseId,
          }),
        );
      } else {
        dispatch(
          createSubTaskInProjectTemplate({
            parentTaskId: task.parentTask.taskId,
          }),
        );
      }
    });

    return Promise.resolve();
  };

  const onTogglePrivate = () => update({ private: !task.private });

  const onToggleTaskType = () =>
    update({
      type:
        task.type === TaskType.Milestone ? TaskType.Task : TaskType.Milestone,
    });

  const onUpdateDescription = (taskDescription: string) =>
    update({ taskDescription });

  const onUpdatePrivateNote = (privateTaskDescription: string) =>
    update({ privateTaskDescription });

  const addSubTask = () => {
    const id = floatUid();

    dispatch(
      createSubTaskInProjectTemplate({
        parentTaskId: task.taskId,
        payload: { taskId: id },
      }),
    );
    openGridAutoColumnCell(id);
    queueMacrotask(() =>
      (
        document.querySelector(
          `[data-row-key="TASK-${task.taskId}"] .js__expand`,
        ) as HTMLButtonElement
      )?.click(),
    );
  };

  const onUpdateParent = ({ id, type }) => {
    if (type === 'PROJECT_PHASE') {
      dispatch(
        updateProjectTemplateParentOfTask({
          task,
          projectPhase: {
            projectPhaseId: id,
          },
        }),
      );
    } else {
      dispatch(
        updateProjectTemplateParentOfTask({
          task,
          parentTask: {
            taskId: id,
          },
        }),
      );
    }
  };

  const onAddTaskBelow = () => {
    const id = floatUid();

    dispatch(
      addTaskBelowProjectTemplate({
        taskId: task.taskId,
        payload: { taskId: id },
      }),
    );
    openGridAutoColumnCell(id);
  };

  const onAddTaskAbove = () => {
    const id = floatUid();

    dispatch(
      addTaskAboveProjectTemplate({
        taskId: task.taskId,
        payload: {
          taskId: id,
        },
      }),
    );
    openGridAutoColumnCell(id);
  };

  const onCreateDependency = (dependentTask) => {
    dispatch(
      addTaskDependencyInProjectTemplate({
        taskId: task.taskId,
        dependentTaskId: dependentTask.taskId,
      }),
    );
  };

  const onUpdateCSATState = (checked) => {
    update({
      isCsatEnabled: checked,
    });
  };

  const findPrivateSpaceId = (): number | undefined => {
    const privateSpace = spaces.find((space) => space.private);

    return privateSpace?.spaceId;
  };

  /**
   * @param data - param from mentions component
   * @param isContextPrivate - check whether mentions happens on a private context, if yes, add doc to private space tab.
   * Method that checks if the space tab with a document template is already set.
   * If it is already existing, we ignore
   * If that is not existing but the space was not setup we ignore too.
   * Otherwise it gets pushed to the space tab of the phase.
   */
  const onAddMention = (data: MentionItem, { isContextPrivate }) => {
    // Filter out already added space tabs
    const docAlreadyExist = spacesOfTemplate.some(
      (spaceTab) =>
        spaceTab.url?.includes(`/templates/${data.objectId}`) ||
        spaceTab.spaceTabId === data.objectId,
    );

    if (
      !docAlreadyExist &&
      spaceSetup === 'COMPLETE' &&
      data.type === MentionType.SpaceTab &&
      taskProjectPhase
    ) {
      const privateSpaceId = findPrivateSpaceId();
      const spaceId =
        isContextPrivate && privateSpaceId
          ? privateSpaceId
          : taskProjectPhase.projectPhaseId;

      dispatch(
        addDocumentFromTemplateAction({
          spaceId,
          template: {
            templateId: data.objectId,
            templateName: data.name,
          },
        }),
      );
    }
  };

  const onChangeResponsibility = (team: MemberType[]) => {
    update({
      responsibleOn: team,
    });
  };

  const onJiraFormChange = (data: UpdateJiraMappingsInTemplateAction) => {
    dispatch(updateJiraMappingsInTemplateAction(data));
  };

  return {
    addSubTask,
    onToggleTaskType,
    onTogglePrivate,
    onUpdateName,
    onToggleTaskTemplate,
    onUpdateNameAndAddRow,
    onUpdateDescription,
    onUpdatePrivateNote,
    onDeleteTask,
    onUpdateTask: update,
    onUpdateParent,
    onAddTaskBelow,
    onAddTaskAbove,
    onUpdateAssignee,
    onCreateDependency,
    onUpdateCSATState,
    onAddMention,
    onChangeResponsibility,
    onJiraFormChange,
  };
};
